import { ReadContractorVisit } from "@/types/contractorVisit";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import { DateTime } from "luxon";
import { useGeolocationPermissions } from "@/hooks/useGeolocationPermissions";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MobileListItemBadge } from "@/components/mobile-list";
import { getStatusBadgeVariant } from "../utils/getStatusBadgeVariant";
import {
  Calendar,
  Clock,
  User,
  Briefcase,
  MapPin,
  CheckCircle,
  Smartphone,
  Monitor,
  Signal,
} from "lucide-react";
import {
  VISIT_STATUS_STARTED,
  VISIT_STATUS_COMPLETED,
} from "@/schemas/contractorVisit";
import { DetailItem } from "./DetailItem";
import { InitializeVisitButton } from "./InitializeVisitButton";
import { GeolocationAlert } from "./GeolocationAlert";
import { GeolocationProvider } from "@/contexts/GeolocationContext";
import { useFormatters } from "@/lib/hooks/useFormatters";
import { DateTimePicker } from "@/components/DateTimePicker";
import { isEmpty } from "@/lib/utils/isEmpty";

interface VisitDetailsProps {
  visit: ReadContractorVisit;
  onInitializeVisit?: () => void;
  onCompleteVisit?: (startTime?: string, endTime?: string) => void;
  loading?: boolean;
}

const VisitDetailsContent = ({
  visit,
  onInitializeVisit,
  onCompleteVisit,
  loading,
}: VisitDetailsProps) => {
  const {
    formatDateTime,
    formatDuration,
    calculateRealDuration,
    formatCoordinates,
  } = useFormatters();
  const { t } = useTranslation();
  const [realDuration, setRealDuration] = useState<string>("");
  const [startTimeInput, setStartTimeInput] = useState<string>("");
  const [endTimeInput, setEndTimeInput] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");
  const {
    isEnabled: isGeolocationEnabled,
    isSupported: isGeolocationSupported,
  } = useGeolocationPermissions();

  useEffect(() => {
    const updateRealDuration = () => {
      if (visit.start_datetime) {
        setRealDuration(
          calculateRealDuration(visit.start_datetime, visit.end_datetime),
        );
      }
    };

    updateRealDuration();

    const interval = setInterval(updateRealDuration, 60000);

    return () => clearInterval(interval);
  }, [calculateRealDuration, visit.start_datetime, visit.end_datetime]);

  useEffect(() => {
    if (visit.start_datetime) {
      setStartTimeInput(formatDateTime(visit.start_datetime));
    }
    if (visit.end_datetime) {
      setEndTimeInput(formatDateTime(visit.end_datetime));
    }
  }, [visit.start_datetime, visit.end_datetime, formatDateTime]);

  const canModifyVisit = isGeolocationSupported && isGeolocationEnabled;
  const canContractorEditHours = visit.hours_editable_by_contractor;
  const canContractorConfirmVisit = visit.confirmable_by_contractor;

  const validateDateTimeInputs = useCallback(
    (start: string, end: string): string | null => {
      const startDateTime = DateTime.fromISO(start);
      const endDateTime = DateTime.fromISO(end);

      if (!startDateTime.isValid || !endDateTime.isValid) {
        return t("contractorVisits.error.invalidDateTime");
      }

      if (endDateTime <= startDateTime) {
        return t("contractorVisits.error.endBeforeStart");
      }

      return null;
    },
    [t],
  );

  const handleCompleteVisit = () => {
    if (!onCompleteVisit) return;

    if (canContractorEditHours) {
      if (!startTimeInput || !endTimeInput) return;

      const error = validateDateTimeInputs(startTimeInput, endTimeInput);
      if (error) {
        setValidationError(error);
        return;
      }

      setValidationError("");
      onCompleteVisit(startTimeInput, endTimeInput);
    } else {
      setValidationError("");
      onCompleteVisit();
    }
  };

  useEffect(() => {
    if (canContractorEditHours && startTimeInput && endTimeInput) {
      const error = validateDateTimeInputs(startTimeInput, endTimeInput);
      setValidationError(error || "");
    } else {
      setValidationError("");
    }
  }, [
    startTimeInput,
    endTimeInput,
    canContractorEditHours,
    t,
    validateDateTimeInputs,
  ]);

  const hasStartCoordinates =
    !isEmpty(visit.start_latitude) && !isEmpty(visit.start_longitude);
  const hasConfirmationCoordinates =
    !isEmpty(visit.confirmation_latitude) &&
    !isEmpty(visit.confirmation_longitude);

  const DeviceIcon = visit.device_type === "mobile" ? Smartphone : Monitor;

  const geolocationAccuracy =
    visit.status === VISIT_STATUS_COMPLETED
      ? visit.confirmation_geolocation_accuracy
      : visit.status === VISIT_STATUS_STARTED
        ? visit.start_geolocation_accuracy
        : null;

  const hasAnyTimeData =
    visit.start_datetime ||
    visit.end_datetime ||
    visit.started_at ||
    visit.ended_at ||
    canModifyVisit;

  const isVisitInProgress =
    Boolean(visit.start_datetime) && visit.status === VISIT_STATUS_STARTED;

  const shouldShowCompleteForm =
    isVisitInProgress &&
    onCompleteVisit &&
    canContractorConfirmVisit &&
    visit.edit_allowed;
  const shouldShowInitializeButton =
    !visit.start_datetime &&
    onInitializeVisit &&
    canContractorConfirmVisit &&
    visit.edit_allowed;

  return (
    <div className="space-y-6 mt-6">
      <div className="space-y-3">
        <div>
          <h2 className="text-xl font-semibold leading-tight">
            {visit.project_name}
          </h2>
          <p className="text-sm font-semibold text-muted-foreground mt-1">
            {visit.store_name}
          </p>
        </div>
      </div>

      <div className="space-y-0">
        <DetailItem
          icon={CheckCircle}
          label={t("contractorVisits.fields.status")}
          value={
            <MobileListItemBadge variant={getStatusBadgeVariant(visit.status)}>
              {t(`contractorVisitsStatuses.${visit.status}`)}
            </MobileListItemBadge>
          }
        />

        <DetailItem
          icon={User}
          label={t("contractorVisits.fields.contractor_name")}
          value={visit.contractor_full_name}
        />

        <DetailItem
          icon={Briefcase}
          label={t("contractorVisits.fields.project_role_name")}
          value={visit.project_role_name}
        />

        <DetailItem
          icon={Calendar}
          label={t("contractorVisits.fields.date")}
          value={visit.date}
        />
      </div>

      {hasAnyTimeData && (
        <div className="space-y-0">
          {canContractorEditHours ? (
            <>
              {visit.start_datetime && (
                <DetailItem
                  icon={Clock}
                  label={t("contractorVisits.fields.start_datetime")}
                  value={formatDateTime(visit.start_datetime)}
                />
              )}
              {visit.end_datetime && (
                <DetailItem
                  icon={Clock}
                  label={t("contractorVisits.fields.end_datetime")}
                  value={formatDateTime(visit.end_datetime)}
                />
              )}
            </>
          ) : (
            <>
              {visit.started_at && (
                <DetailItem
                  icon={Clock}
                  label={t("contractorVisits.fields.started_at")}
                  value={formatDateTime(visit.started_at)}
                />
              )}
              {visit.ended_at && (
                <DetailItem
                  icon={Clock}
                  label={t("contractorVisits.fields.ended_at")}
                  value={formatDateTime(visit.ended_at)}
                />
              )}
            </>
          )}

          {visit.planned_duration_visible && visit.planned_duration && (
            <DetailItem
              icon={Clock}
              label={t("contractorVisits.fields.planned_duration")}
              value={formatDuration(visit.planned_duration)}
            />
          )}

          {visit.planned_duration_visible && visit.start_datetime && (
            <DetailItem
              icon={CheckCircle}
              label={t("contractorVisits.fields.real_duration")}
              value={realDuration}
              highlight
            />
          )}

          {visit.confirmation_user_name && (
            <DetailItem
              icon={User}
              label={t("contractorVisits.fields.confirmation_user_name")}
              value={visit.confirmation_user_name}
            />
          )}

          {hasStartCoordinates && (
            <DetailItem
              icon={MapPin}
              label={t("contractorVisits.fields.visit_start_coordinates")}
              value={formatCoordinates(
                visit.start_latitude,
                visit.start_longitude,
              )}
            />
          )}

          {hasConfirmationCoordinates && (
            <DetailItem
              icon={MapPin}
              label={t("contractorVisits.fields.visit_end_coordinates")}
              value={formatCoordinates(
                visit.confirmation_latitude,
                visit.confirmation_longitude,
              )}
            />
          )}

          {geolocationAccuracy && (
            <DetailItem
              icon={Signal}
              label={t("contractorVisits.fields.geolocation_accuracy")}
              value={t(
                `contractorVisits.geolocationAccuracy.${geolocationAccuracy}`,
              )}
            />
          )}

          {visit.device_type && (
            <DetailItem
              icon={DeviceIcon}
              label={t("contractorVisits.fields.device_type")}
              value={t(`contractorVisits.deviceType.${visit.device_type}`)}
            />
          )}
        </div>
      )}

      {shouldShowCompleteForm && (
        <div className="space-y-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-blue-900">
            {t("contractorVisits.completeVisit.title")}
          </h3>

          <div className="space-y-4">
            {canContractorEditHours && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start-time" className="text-blue-900">
                    {t("contractorVisits.fields.start_datetime")}
                  </Label>
                  <DateTimePicker
                    name="start_datetime"
                    value={startTimeInput}
                    onChange={setStartTimeInput}
                    onBlur={() => setStartTimeInput(startTimeInput)}
                    className="mt-1 w-full h-12"
                  />
                </div>

                <div>
                  <Label htmlFor="end-time" className="text-blue-900">
                    {t("contractorVisits.fields.end_datetime")}
                  </Label>
                  <DateTimePicker
                    name="end_datetime"
                    value={endTimeInput}
                    onChange={setEndTimeInput}
                    onBlur={() => setEndTimeInput(endTimeInput)}
                    className="mt-1 w-full h-12"
                  />
                </div>
              </div>
            )}

            {validationError && (
              <div className="text-red-600 text-sm font-medium">
                {validationError}
              </div>
            )}

            <Button
              onClick={handleCompleteVisit}
              className="w-full h-12 text-base font-medium"
              size="lg"
              disabled={
                (canContractorEditHours &&
                  (!startTimeInput || !endTimeInput)) ||
                loading ||
                !canModifyVisit ||
                !!validationError
              }
            >
              {t("contractorVisits.completeVisit.button")}
            </Button>
          </div>
        </div>
      )}

      {shouldShowInitializeButton && (
        <div className="pt-4 space-y-3">
          <InitializeVisitButton
            onClick={onInitializeVisit}
            disabled={!canModifyVisit || loading}
          />

          <GeolocationAlert />
        </div>
      )}
    </div>
  );
};

export const VisitDetails = (props: VisitDetailsProps) => {
  return (
    <GeolocationProvider>
      <VisitDetailsContent {...props} />
    </GeolocationProvider>
  );
};
